import axios from 'axios';
import { get } from 'lodash-es';
import { from } from 'rxjs';

import { getShipmentUsageDetails } from '@shipstation/client-common/api/shipment-usage-details.api';
import { rebrandCarrierProviderNames } from '../../components/data-driven-logo/name-utils';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { manageSubscription as manageSubscriptionApi } from '../../pages/settings/api/subscription';

export const getHasViewedUpsInvoicedModal = () => {
  const request = axios.get('/seller/settings/ups_invoiced').then((response) => response?.data ?? false);
  return from(request);
};

export const getSeller = () => {
  const request = axios.get('/seller').then((response) => {
    return get(response, 'data', undefined);
  });

  const seller$ = from(request);
  return seller$;
};

export const getShipFroms = () => {
  const request = axios.get('/seller/shipfrom').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const getPackages = () => {
  const request = axios.get('/seller/packagetypes').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const getLabelProviders = (homeCountry) => {
  const request = axios.get('/seller/labelproviders').then((response) => {
    const { data } = response ?? {};
    if (data && homeCountry) {
      return rebrandCarrierProviderNames(data, homeCountry);
    }
    return data;
  });

  const data$ = from(request);
  return data$;
};

export const getServices = () => {
  const request = axios.get('/seller/services').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

/**
 * @typedef {import('../../prop-types/service-collection').ServiceCollection} ServiceCollection
 * @returns {Promise<ServiceCollection[]>}
 */
export const getServiceCollections = () => {
  return axios.get('/serviceCollection').then((response) => response.data);
};

export const getStoresPromise = () => {
  return axios.get('/seller/stores').then((response) => {
    return get(response, 'data', undefined);
  });
};

export const getStores = () => {
  return from(getStoresPromise());
};

export const getSellerBrands = () => {
  const request = axios
    .get('/seller/sellerBrands')
    .then((response) => get(response, 'data', undefined))
    .catch(() => undefined);

  const data$ = from(request);
  return data$;
};

export const getShipmentTemplates = () => {
  const request = axios.get('/emailtemplate/shipment').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const getDeliveryTemplates = () => {
  const request = axios.get('/emailtemplate/delivery').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const getHtmlEmailTemplates = () => {
  const request = axios.get('/templates/emails').then((response) => get(response, 'data', []));
  return from(request);
};

export const getPackingSlips = () => {
  const request = axios.get('/packingslip').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const getWorkstations = () => {
  const request = axios.get('/seller/workstations').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const getSettingsRaw = () => {
  return axios.get('/seller/settings', {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Expires: 0,
    },
  });
};

export const getSettings = () => {
  const request = getSettingsRaw().then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const checkUpsLabelTermsRequired = async () => {
  const response = await axios.get('/seller/ups-label-terms-required');
  return response.data;
};

export const refreshStore = (storeGuid) => {
  const request = axios.post(`/store/${storeGuid}/refresh`).then((response) => get(response, 'data', undefined));

  const data$ = from(request);
  return data$;
};

export const refreshAllStores = () => {
  const request = axios.post('/store/refresh').then((response) => get(response, 'data', undefined));

  const data$ = from(request);
  return data$;
};

/**
 * Refresh specific orders for a store
 * @param {string} storeGuid - The store GUID
 * @param {number[]} orderIds - Array of order IDs to refresh
 * @returns {Observable} Observable for epic consumption
 */
export const refreshStoreOrders = (storeGuid, orderIds) =>
  from(
    axios.post(`/store/${storeGuid}/refresh`, { orderIds }).then((response) => {
      return get(response, 'data', { storeGuid, orderIds });
    }),
  );

export const refreshProducts = async (storeGuid, refreshOnlyLastUpdated = false) => {
  const queryString = refreshOnlyLastUpdated ? '?RefreshOnlyLastUpdated=true' : '';
  const response = await axios.post(`/store/${storeGuid}/refresh/products${queryString}`);
  return response.data;
};

export const refreshInventory = async (storeGuid) => {
  const response = await axios.post(`/store/${storeGuid}/refresh/inventory`);
  return response.data;
};

export const getPackageSets = () => {
  const getAndTransform = axios.get('/packageset').then((response) => {
    return get(response, 'data', emptyArray);
  });

  const packageSets$ = from(getAndTransform);
  return packageSets$;
};

export const saveShipFromLocation = (shipFrom) => {
  const post = axios.post('/seller/shipFrom', shipFrom).then((response) => get(response, 'data', emptyObject));

  const save$ = from(post);
  return save$;
};

export const getStoreLogs = (storeId) => {
  const request = axios.get(`/store/${storeId}/refreshErrors`).then((response) => get(response, 'data'));
  return from(request);
};

export const getFulfillmentProviders = (showInactive = false) => {
  const request = axios.get(`/seller/fulfillmentproviders?showInactive=${showInactive}`).then((response) => {
    return get(response, 'data', undefined);
  });
  const data$ = from(request);
  return data$;
};

export const getPendingFulfillmentPartners = () => {
  const request = axios.get('/seller/pendingFulfillmentPartners').then((response) => {
    return get(response, 'data', undefined);
  });
  const data$ = from(request);
  return data$;
};

export const getFulfillmentProviderServices = () => {
  const request = axios.get('/seller/fulfillmentservices').then((response) => {
    return get(response, 'data', undefined);
  });
  const data$ = from(request);
  return data$;
};

export const acceptDhlWalletTerms = () => {
  const request = axios.patch('/seller/accept-terms/dhl-wallet').then((response) => {
    return response;
  });
  const data$ = from(request);
  return data$;
};

export const manageSubscription = () => {
  const request = manageSubscriptionApi();
  const data$ = from(request);
  return data$;
};

export const getShipmentUsage = () => {
  const request = getShipmentUsageDetails();
  const data$ = from(request);
  return data$;
};
